import { FieldArray, Form, Formik } from 'formik'
import { useState } from 'react'

import type { ReactElement } from 'react'
import type { IAppSummary } from '~/types/application'
import type { IEngineFormValues, IEngine, IEngineMapping, IVersionEngine } from '~/types/arbitration'

import ActionHeader from '../components/ActionHeader'
import PlacementCard from '../components/PlacementCard'
import { validationSchema } from '../utils'

import { DEFAULT_ITEMS_PER_PAGE_VALUE } from '~/const'
import { useNavigationGraphsListQuery } from '~/queries/navigation-graphs'
import { usePlacementsQuery } from '~/queries/placements'

interface Props {
  className?: string
  engine?: IEngine | IVersionEngine
  isReviewEngine?: boolean
  onSubmit: (mapping: IEngineMapping, version?: string) => Promise<void>
  selectedApp: IAppSummary
  version?: string
}

const DevReviewEngine = ({
  className,
  engine,
  isReviewEngine,
  onSubmit,
  selectedApp,
  version,
}: Props): ReactElement => {
  const [search, setSearch] = useState('')

  const { data: placements } = usePlacementsQuery({
    appKey: selectedApp?.app_key,
    size: DEFAULT_ITEMS_PER_PAGE_VALUE,
  })

  const { data: navigationGraphs } = useNavigationGraphsListQuery(
    {
      'app-key': selectedApp.app_key,
      'device-type': selectedApp.device_type,
      size: DEFAULT_ITEMS_PER_PAGE_VALUE,
    },
    { enabled: !!selectedApp }
  )

  const placementOptions = (placements?.data && placements.data.map(({ key }) => ({ label: key, value: key }))) || []

  const navigationGraphOptions =
    (navigationGraphs?.data && navigationGraphs.data.map(({ title, uuid }) => ({ label: title, value: uuid }))) || []

  const initialValues: IEngineFormValues = {
    placements: engine?.mapping
      ? Object.entries(engine.mapping).map(([placement, navigationGraph]) => ({
          navigationGraph: navigationGraphOptions.find(({ value }) => value === navigationGraph),
          placement: placementOptions.find(({ value }) => value === placement),
        }))
      : [],
    version: version || '',
  }

  const handleSubmit = async (values: IEngineFormValues) => {
    const mapping = values.placements.reduce((prev, { navigationGraph, placement }) => {
      if (navigationGraph && placement) {
        prev[placement.value] = navigationGraph.value
      }
      return prev
    }, {} as IEngineMapping)

    await onSubmit(mapping, values.version)
  }

  const handleSearch = (value: string) => {
    setSearch(value)
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ dirty, handleSubmit, isSubmitting, resetForm, values }) => (
        <FieldArray
          name="placements"
          render={({ push, remove }) => (
            <Form className={className}>
              {engine ? (
                <ActionHeader
                  addButtonText="Add placement"
                  dirty={dirty}
                  isReviewEngine={isReviewEngine}
                  isSubmitting={isSubmitting}
                  metadata={{ updatedAt: engine.updated_at_ts, updatedBy: engine.updated_by }}
                  onAdd={() => push({ navigationGraph: undefined, placement: undefined })}
                  onSave={handleSubmit}
                  onSearch={handleSearch}
                  onUndo={resetForm}
                />
              ) : null}
              <div className="engine-placements-container">
                {values.placements.map(({ placement }, index) =>
                  !placement || placement?.label.includes(search) ? (
                    <div className="engine-placements-container__row" key={index}>
                      <PlacementCard
                        navigationGraphName={`placements[${index}].navigationGraph`}
                        navigationGraphOptions={navigationGraphOptions}
                        onDelete={() => remove(index)}
                        placementName={`placements[${index}].placement`}
                        placementOptions={placementOptions}
                      />
                    </div>
                  ) : null
                )}
              </div>
            </Form>
          )}
        />
      )}
    </Formik>
  )
}

export default DevReviewEngine
